/* eslint-disable */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'blockquote',
  'span',
  'div',
  'progress',
  'h1',
  'h2',
  'h3',
];

const ALLOWED_ATTR = ['href', 'src', 'alt', 'target', 'rel', 'class', 'data-filename', 'value', 'max'];

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/** Multer/busboy often misreads UTF-8 multipart filenames as Latin-1 */
export function fixMojibakeFilename(name) {
  if (!name || typeof name !== 'string') return name;
  if (!/[\u00C0-\u00FF]/.test(name)) return name;
  try {
    const bytes = Uint8Array.from(name, (c) => c.charCodeAt(0) & 0xff);
    const fixed = new TextDecoder('utf-8').decode(bytes);
    if (fixed.includes('\uFFFD')) return name;
    return fixed;
  } catch {
    return name;
  }
}

export function resolveBaiboonMediaUrl(url) {
  if (!url) return url;
  const raw = String(url).trim();
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (parsed.pathname.includes('/api/baiboon/media/')) {
        return `${API_URL}${parsed.pathname}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  if (raw.startsWith('/api/baiboon/media/')) {
    return `${API_URL}${raw}`;
  }
  return raw;
}

export function isBaiboonMediaUrl(url) {
  if (!url) return false;
  return String(url).includes('/api/baiboon/media/');
}

export function normalizeAnswerMedia(html) {
  if (!html || typeof document === 'undefined') return html;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  wrap.querySelectorAll('img[src]').forEach((img) => {
    img.setAttribute('src', resolveBaiboonMediaUrl(img.getAttribute('src')));
  });
  wrap.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('href', resolveBaiboonMediaUrl(a.getAttribute('href')));
    const dataName = a.getAttribute('data-filename');
    if (dataName) a.setAttribute('data-filename', fixMojibakeFilename(dataName));
    if (a.textContent) a.textContent = fixMojibakeFilename(a.textContent);
  });
  return wrap.innerHTML;
}

/** When Quill link href changes but visible URL text stays old, sync them */
export function syncLinkDisplayText(html) {
  if (!html || typeof document === 'undefined') return html;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  let changed = false;
  wrap.querySelectorAll('a[href]').forEach((a) => {
    if (a.classList.contains('baiboon-file')) return;
    const href = (a.getAttribute('href') || '').trim();
    const text = (a.textContent || '').trim();
    if (!href || !/^https?:\/\//i.test(text) || text === href) return;
    a.textContent = href;
    changed = true;
  });
  return changed ? wrap.innerHTML : html;
}

/** Re-apply baiboon-file markers — Quill strips custom classes on save */
export function markBaiboonFileLinks(html) {
  if (!html || typeof document === 'undefined') return html;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  let changed = false;
  wrap.querySelectorAll('a[href]').forEach((a) => {
    const href = resolveBaiboonMediaUrl(a.getAttribute('href') || '');
    if (!isBaiboonMediaUrl(href)) return;
    a.setAttribute('href', href);
    if (!a.classList.contains('baiboon-file')) {
      a.classList.add('baiboon-file');
      changed = true;
    }
    if (!a.getAttribute('data-filename')) {
      const name = fixMojibakeFilename((a.textContent || '').trim() || href.split('/').pop() || 'ไฟล์แนบ');
      a.setAttribute('data-filename', name);
      changed = true;
    }
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  return changed ? wrap.innerHTML : html;
}

export function finalizeAnswerHtml(html) {
  if (!html) return '';
  return markBaiboonFileLinks(syncLinkDisplayText(normalizeAnswerMedia(html)));
}

export function isPdfUrl(url, label = '') {
  const raw = `${url || ''} ${label || ''}`.toLowerCase();
  return raw.includes('.pdf') || raw.includes('application/pdf');
}

/** Chat-only: turn PDF links into preview cards (iframe thumbnail + filename) */
export function enhancePdfPreviewCards(html) {
  if (!html || typeof document === 'undefined') return html;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;

  wrap.querySelectorAll('a[href]').forEach((a) => {
    const href = resolveBaiboonMediaUrl(a.getAttribute('href') || '');
    const name = fixMojibakeFilename(
      a.getAttribute('data-filename') || (a.textContent || '').trim() || href.split('/').pop() || 'ไฟล์ PDF'
    );
    if (!isPdfUrl(href, name)) return;
    if (a.classList.contains('baiboon-pdf-card')) return;

    a.classList.add('baiboon-file', 'baiboon-pdf-card');
    a.setAttribute('href', href);
    a.setAttribute('data-filename', name);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.textContent = '';

    const head = document.createElement('span');
    head.className = 'baiboon-pdf-card__head';

    const icon = document.createElement('span');
    icon.className = 'baiboon-pdf-card__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'PDF';

    const title = document.createElement('span');
    title.className = 'baiboon-pdf-card__name';
    title.textContent = name;

    head.appendChild(icon);
    head.appendChild(title);

    const preview = document.createElement('span');
    preview.className = 'baiboon-pdf-card__preview';

    const hint = document.createElement('span');
    hint.className = 'baiboon-pdf-card__hint';
    hint.textContent = 'แตะเพื่อเปิดดู';

    preview.appendChild(hint);
    a.appendChild(head);
    a.appendChild(preview);
  });

  return wrap.innerHTML;
}

export function isHtmlAnswer(value) {
  if (!value || typeof value !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(value);
}

export function stripAnswerText(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function hasAnswerContent(value) {
  if (!value || typeof value !== 'string') return false;
  if (stripAnswerText(value).length > 0) return true;
  if (/<img[\s>]/i.test(value)) return true;
  if (/class=["'][^"']*baiboon-file/i.test(value)) return true;
  return /href=["'][^"']*\/api\/baiboon\/media\//i.test(value);
}

export function sanitizeAnswerHtml(html) {
  if (!html) return '';
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
  });

  if (typeof document === 'undefined') return clean;

  const wrap = document.createElement('div');
  wrap.innerHTML = clean;
  wrap.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  wrap.querySelectorAll('img').forEach((img) => {
    img.setAttribute('loading', 'lazy');
  });
  return finalizeAnswerHtml(wrap.innerHTML);
}
